import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, RefreshCw } from "lucide-react";
import { getDataQualityStats } from "@/lib/seo-quality.functions";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";

export const Route = createFileRoute("/dashboard/admin/data-quality")({
 head: () => ({ meta: [{ title: "Data Quality, Heallexa Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
 component: DataQualityPage,
});

type Stats = Awaited<ReturnType<typeof getDataQualityStats>>;

function DataQualityPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const [stats, setStats] = useState<Stats | null>(null);
 const [busy, setBusy] = useState(true);

 useEffect(() => {
 if (loading) return;
 if (!user || primaryRole !== "admin") { navigate({ to: "/login" }); return; }
 getDataQualityStats().then(setStats).finally(() => setBusy(false));
 }, [loading, user, primaryRole, navigate]);

 if (loading || busy) {
 return <div><div className="pt-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></div>;
 }

 const rows: Array<[string, number]> = stats ? [
 ["Total providers (not deleted)", stats.total],
 ["Missing specialty", stats.missingSpecialty],
 ["Missing city", stats.missingCity],
 ["Missing state", stats.missingState],
 ["Missing phone", stats.missingPhone],
 ["Missing website", stats.missingWebsite],
 ["Missing email", stats.missingEmail],
 ["No insurance listed", stats.missingInsurance],
 ["Unclaimed listings", stats.unclaimed],
 ["Claimed listings", stats.claimed],
 ] : [];

 return (
 <AdminPageShell
   title="Data Quality"
   description="Field completeness across the active provider directory."
   primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: () => { setBusy(true); getDataQualityStats().then(setStats).finally(() => setBusy(false)); } }}
 >
   <div className="card-surface-flat rounded-md">
     <table className="w-full text-sm">
       <tbody className="divide-y divide-border">
         {rows.map(([label, val]) => (
           <tr key={label}>
             <td className="px-4 py-3">{label}</td>
             <td className="px-4 py-3 text-right font-mono">{val.toLocaleString()}</td>
           </tr>
         ))}
       </tbody>
     </table>
   </div>
 </AdminPageShell>
 );
}
